import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Images } from "lucide-react";
import Progress from "../ui/Progress";
import StatusLabel from "../ui/StatusLabel";
import { isPdfBook } from "../../util/bookFormat";
import Popup from "../ui/Popup";
import Button from "../ui/Button";
import { saveStep } from "../../services/stepService";
import { markStepAsVisited } from "../../redux/slice/profileSlice";
import { loadPendingImages } from "../../util/pendingImageStorage";
import { useBuilderNav } from "./BuilderNavContext";
import { BUILDER_STEPS, STEP_BY_CODE as stepMap, MAX_PHOTOS, stepForLocation } from "./builderSteps";

/**
 * The builder's progress in the header: the redesigned `Progress` with the
 * navigation rules from `common/StepProgressView.js` (which the steps not
 * yet rebuilt still render inline).
 *
 * Rules kept as they were: the current step is never clickable; steps 3 to 5
 * need a cover photo; a step is clickable going back, or forward to one
 * already visited; going forward with more than 20 photos not yet sent for
 * drawing is stopped with a pop-up. Nothing is clickable while the step is
 * busy saving photos (`useBuilderBusy`). A step can register an interceptor (see
 * `BuilderNavContext`) to run work before a forward click goes through.
 */
export { BUILDER_STEPS, stepForLocation } from "./builderSteps";

const BuilderProgress = ({ className }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tag = searchParams.get("tag");
  const nav = useBuilderNav();
  const { session } = useSelector((state) => state.authProvider);
  const { activeBookId } = useSelector((state) => state.stepFlow);
  const { myBooks = [], data: profileData } = useSelector((state) => state.profile);
  const { coverImages = [], images = [] } = useSelector((state) => state.sketch);
  const [tooMany, setTooMany] = useState(0);

  const activeBook = myBooks.find((book) => book._id === activeBookId);
  const profileBook = profileData?.bookData?.find((book) => book._id === activeBookId);
  const latest = profileBook?.latestStepCode;
  const latestCode = typeof latest === "object" ? latest?.code : latest;

  const current =
    stepForLocation(location.pathname, location.search) ||
    (latestCode && stepMap[latestCode]) ||
    (coverImages.length ? 1 : 2);

  const visited = (activeBook?.completedSteps || [])
    .map((step) => stepMap[typeof step === "object" ? step?.code : step])
    .filter(Boolean);

  const canGoTo = (n) => {
    if (n === current || nav?.busy) return false;
    if (n >= 3 && !coverImages.length) return false;
    return n <= current || visited.includes(n);
  };

  const pendingNotSentCount = () => {
    const pending = loadPendingImages(activeBookId);
    return images.filter((img) => {
      if (img?.meta?.aws_upload?.s3_url || img?.AIUrl) return false;
      const entry = pending.find((p) => p.id === img.id || p._id === img._id);
      return !entry?.sentToRun;
    }).length;
  };

  const go = (step) => {
    saveStep({ userId: session?.user?._id, bookId: activeBookId, isCompleted: "yes", stepCode: step.stepCode });
    dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: step.stepCode }));
    navigate(`${step.route}?stepCode=${step.stepCode}${tag ? `&tag=${tag}` : ""}`);
  };

  const onGoTo = (n) => {
    const step = BUILDER_STEPS[n - 1];
    if (!step || !canGoTo(n)) return;
    if (n > current) {
      const count = pendingNotSentCount();
      if (count > MAX_PHOTOS) {
        setTooMany(count);
        return;
      }
    }
    const interceptor = nav?.getInterceptor?.();
    if (interceptor) {
      interceptor({ stepNumber: n, stepCode: step.stepCode, route: step.route, navigate: () => go(step) });
    } else {
      go(step);
    }
  };

  return (
    <>
      <Progress
        step={current}
        canGoTo={canGoTo}
        onGoTo={onGoTo}
        // PreviewPdf: on phones the "PDF only" tag sits beside the step name on Preview (only there, decided 2026-10-26).
        mobileLabel={
          current === 5 && isPdfBook(activeBook) && !activeBook?.gift?.giftOrderId ? (
            <span className="inline-flex items-center gap-2">
              Preview
              <StatusLabel status="pdf" />
            </span>
          ) : undefined
        }
        className={className}
      />
      <Popup
        open={tooMany > 0}
        onClose={() => setTooMany(0)}
        icon={<Images className="h-7 w-7" />}
        title="That's a few too many"
        actions={[
          <Button key="ok" onClick={() => setTooMany(0)}>
            Got it
          </Button>,
        ]}
      >
        Your book holds {MAX_PHOTOS} pages. Remove {tooMany - MAX_PHOTOS} {tooMany - MAX_PHOTOS === 1 ? "photo" : "photos"} to continue.
      </Popup>
    </>
  );
};

export default BuilderProgress;
